import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject, of } from 'rxjs';
import { CareerPathwaySkills } from './skills/skills.interface';
import { AddSkillForm } from './skills/add-skill-form/add-skill-form';
import { CareerPathwayProvider } from './providers/career-pathway-provider.interface';
import { PathwayProgram } from './programs/programs.interface';
import { AddProgramForm } from './programs/add-program/add-program-form';
import { CareerPathwayJobs, JobsDomain, JobSummary } from './roles/career-pathway-jobs.interface';
import { CreateJobForm } from './roles/create-job/create-job-form';
import { ClientSetupSettings } from '../client-view/career-pathway-client-setup/client-setup-settings/client-setup-settings.interface';
import { Operation } from 'fast-json-patch';
import { CareerPathwayJobNode } from '@edassist-ui/new-career-pathways/career-pathways-job-node.interface';
import { constants, LocalStorageService } from '@edassist/common';
import { map } from 'rxjs/operators';
import { CacheBucket, withCache } from '@ngneat/cashew';

@Injectable({
	providedIn: 'root'
})
export class CareerPathwaysTabsService {
	private readonly relativeUrl = 'career-pathways';
	public resetTabSubject = new BehaviorSubject<any>(null);
	resetTabs = this.resetTabSubject.asObservable();
	private readonly careerPathwaysConfigBucket = new CacheBucket();

	private careerPathwaysConfigSubject = new BehaviorSubject<ClientSetupSettings | null>(null);

	constructor(private readonly http: HttpClient) {}

	resetTab() {
		this.resetTabSubject.next('close');
	}

	getCareerPathwaysConfigChanges(): Observable<ClientSetupSettings | null> {
		return this.careerPathwaysConfigSubject.asObservable();
	}

	destroyResetTabSubject() {
		this.resetTabSubject.unsubscribe();
	}

	getSkills(clientId?: number): Observable<Array<CareerPathwaySkills>> {
		const params = clientId ? { clientId } : {};

		return this.http.get<Array<CareerPathwaySkills>>(`${this.relativeUrl}/skills`, { params });
	}

	addSkill(skill: AddSkillForm): Observable<AddSkillForm> {
		return this.http.post<AddSkillForm>(`${this.relativeUrl}/skills`, skill);
	}

	getCareerPathwayProviders(clientId?: number): Observable<Array<CareerPathwayProvider>> {
		const params = clientId ? { clientId } : {};

		return this.http.get<Array<CareerPathwayProvider>>(`${this.relativeUrl}/pathway-institutions`, { params });
	}

	getPathwayPrograms(clientId?: number): Observable<Array<PathwayProgram>> {
		const params = clientId ? { clientId } : {};

		return this.http.get<Array<PathwayProgram>>(`${this.relativeUrl}/pathway-programs`, { params });
	}
	getPathwayProgram(id: string): Observable<PathwayProgram> {
		return this.http.get<PathwayProgram>(`${this.relativeUrl}/pathway-programs/${id}`);
	}

	getJobs(clientId: number = 0): Observable<Array<CareerPathwayJobs>> {
		const params = { clientId };

		return this.http.get<Array<CareerPathwayJobs>>(`${this.relativeUrl}/jobs`, {
			params
		});
	}

	getJob(id: string): Observable<CareerPathwayJobs> {
		return this.http.get<CareerPathwayJobs>(`${this.relativeUrl}/jobs/${id}`);
	}

	getJobSummaries(id): Observable<Array<JobSummary>> {
		return this.http.get<Array<JobSummary>>(`${this.relativeUrl}/clients/${id}/job-summaries`);
	}

	createJob(job: CreateJobForm): Observable<CareerPathwayJobs> {
		return this.http.post<CareerPathwayJobs>(`${this.relativeUrl}/jobs`, job);
	}

	getJobsDomains(): Observable<Array<JobsDomain>> {
		return this.http.get<Array<JobsDomain>>(`${this.relativeUrl}/domains`);
	}

	saveCareerPathwaysProvider(providerId: number): Observable<CareerPathwayProvider> {
		return this.http.post<CareerPathwayProvider>(`${this.relativeUrl}/pathway-institutions`, {
			educationalProviderId: providerId
		});
	}

	addProgram(program: AddProgramForm): Observable<AddProgramForm> {
		return this.http.post<AddProgramForm>(`${this.relativeUrl}/pathway-programs`, program);
	}

	getClientConfigs(clientId): Observable<ClientSetupSettings> {
		return this.http.get<ClientSetupSettings>(`${this.relativeUrl}/client-configs/${clientId}`, {
			context: withCache({
				key: `getClientConfigs-${clientId}`,
				bucket: this.careerPathwaysConfigBucket
			})
		});
	}

	saveClientConfigs(configs: ClientSetupSettings): Observable<ClientSetupSettings> {
		this.careerPathwaysConfigBucket.clear();

		return this.http.put<ClientSetupSettings>(`${this.relativeUrl}/client-configs`, configs).pipe(
			map((updatedConfig) => {
				this.careerPathwaysConfigSubject.next(configs); // Notify subscribers

				return updatedConfig;
			})
		);
	}

	addJobs(clientId, jobs: CareerPathwayJobs[]): Observable<CareerPathwayJobs[]> {
		return this.http.post<CareerPathwayJobs[]>(`${this.relativeUrl}/clients/${clientId}/jobs`, jobs);
	}

	saveJob(jobId: string, patchOperationFields: Array<Operation>): Observable<CareerPathwayJobs> {
		return this.http.patch<CareerPathwayJobs>(`${this.relativeUrl}/jobs/${jobId}`, patchOperationFields);
	}

	getJobMappingOptions(clientId: number): Observable<CareerPathwayJobs[]> {
		return this.http.get<CareerPathwayJobs[]>(`${this.relativeUrl}/clients/${clientId}/job-mapping-options`);
	}

	deleteJob(jobId: string): Observable<void> {
		return this.http.delete<void>(`${this.relativeUrl}/jobs/${jobId}`);
	}

	getJobNodes(clientId: string): Observable<CareerPathwayJobNode> {
		return this.http.get<CareerPathwayJobNode>(`${this.relativeUrl}/clients/${clientId}/job-nodes`);
	}

	isCareerPathwaysEnabled(clientId: number): Observable<boolean> {
		let currentClientDetails = LocalStorageService.getItem(constants.localStorageKeys.CLIENT_DETAILS);
		if (
			currentClientDetails?.isCareerPathwaysEnabled !== undefined &&
			currentClientDetails?.isCareerPathwaysEnabled !== null
		) {
			return of(currentClientDetails?.isCareerPathwaysEnabled);
		}

		return this.getClientConfigs(clientId).pipe(
			map((configs) => {
				let clientDetails = LocalStorageService.getItem(constants.localStorageKeys.CLIENT_DETAILS) || {};
				clientDetails.isCareerPathwaysEnabled = configs.enabled;
				LocalStorageService.setItem(constants.localStorageKeys.CLIENT_DETAILS, clientDetails);

				return configs.enabled;
			})
		);
	}
}
